<template>
  <v-container fluid class="company-management-page">
    <section class="page-hero">
      <div>
        <h1 class="page-title">Company Users &amp; Companies</h1>
        <p class="page-subtitle">
          Manage business accounts, company profiles, access status, and API
          credentials
        </p>
      </div>
    </section>

    <v-card class="management-shell glass-card">
      <v-tabs
        v-model="activeTab"
        color="white lighten-4"
        slider-color="primary"
      >
        <v-tab href="#users"><v-icon left>people</v-icon>Company Users</v-tab>
        <v-tab href="#companies"><v-icon left>business</v-icon>Companies</v-tab>

        <v-tab-item value="users">
          <v-card flat class="table-panel">
            <v-card-title>
              <v-text-field
                v-model="userSearch"
                append-icon="search"
                label="Search users"
                single-line
                hide-details
                class="table-search"
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn
                color="black"
                flat
                :loading="loading"
                @click="loadAll"
                icon
              >
                <v-icon>refresh</v-icon>
              </v-btn>
              <v-btn color="success" round dark @click="openCreateUser">
                <v-icon left>person_add</v-icon>Create user
              </v-btn>
            </v-card-title>
            <v-data-table
              :headers="userHeaders"
              :items="filteredUsers"
              :loading="loading"
              class="elevation-1"
            >
              <template slot="items" slot-scope="props">
                <td>{{ props.item.user_provider_wesmart_id }}</td>
                <td>{{ userName(props.item) }}</td>
                <td>{{ phone(props.item.user_provider_wesmart_tel) }}</td>
                <td>{{ props.item.user_provider_wesmart_email || "-" }}</td>
                <td class="text-xs-center">
                  {{ companyCount(props.item.user_provider_wesmart_id) }}
                </td>
                <td>
                  <v-chip
                    small
                    :color="
                      userStatusColor(props.item.user_provider_wesmart_status)
                    "
                    text-color="white"
                  >
                    {{
                      userStatusLabel(props.item.user_provider_wesmart_status)
                    }}
                  </v-chip>
                </td>
                <td class="justify-center layout px-0">
                  <v-btn icon @click="openEditUser(props.item)"
                    ><v-icon small>edit</v-icon></v-btn
                  >
                  <v-btn
                    icon
                    color="error"
                    @click="askDelete('user', props.item)"
                    ><v-icon small>delete</v-icon></v-btn
                  >
                </td>
              </template>
              <template slot="no-data"
                ><div class="pa-4 text-xs-center">
                  No company users found
                </div></template
              >
            </v-data-table>
          </v-card>
        </v-tab-item>

        <v-tab-item value="companies">
          <v-card flat class="table-panel">
            <v-card-title>
              <v-text-field
                v-model="companySearch"
                append-icon="search"
                label="Search companies"
                single-line
                hide-details
                class="table-search"
              ></v-text-field>
              <v-spacer></v-spacer>
              <v-btn
                color="black"
                flat
                :loading="loading"
                @click="loadAll"
                icon
              >
                <v-icon>refresh</v-icon>
              </v-btn>
              <v-btn color="success" round dark @click="openCreateCompany">
                <v-icon left>add_business</v-icon>Create company
              </v-btn>
            </v-card-title>
            <v-data-table
              :headers="companyHeaders"
              :items="filteredCompanies"
              :loading="loading"
              class="elevation-1"
            >
              <template slot="items" slot-scope="props">
                <td>{{ props.item.company_provider_wesmart_id }}</td>
                <td>{{ props.item.company_provider_wesmart_name }}</td>
                <td>
                  {{ ownerName(props.item.company_provider_wesmart_user_id) }}
                </td>
                <td>{{ phone(props.item.company_provider_wesmart_tel) }}</td>
                <td class="text-xs-right">
                  <div
                    class="company-credit-balance"
                    :class="{
                      'company-credit-balance--negative':
                        Number(props.item.credit_available_balance) < 0,
                    }"
                  >
                    {{ money(props.item.credit_available_balance) }}
                  </div>
                  <div
                    v-if="Number(props.item.credit_reserved_balance || 0) > 0"
                    class="company-credit-reserved"
                  >
                    Reserved {{ money(props.item.credit_reserved_balance) }}
                  </div>
                </td>
                <td>
                  <v-chip
                    small
                    :color="
                      companyStatusColor(
                        props.item.company_provider_wesmart_status
                      )
                    "
                    text-color="white"
                  >
                    {{
                      companyStatusLabel(
                        props.item.company_provider_wesmart_status
                      )
                    }}
                  </v-chip>
                </td>
                <td>{{ secretUpdatedAt(props.item) }}</td>
                <td class="justify-center layout px-0">
                  <v-tooltip top>
                    <v-btn
                      icon
                      slot="activator"
                      @click="openEditCompany(props.item)"
                      ><v-icon small>edit</v-icon></v-btn
                    >
                    <span>Edit company</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <v-btn
                      icon
                      slot="activator"
                      color="warning"
                      @click="askRotate(props.item)"
                      ><v-icon small>vpn_key</v-icon></v-btn
                    >
                    <span>Rotate secret key</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <v-btn
                      icon
                      slot="activator"
                      color="error"
                      @click="askDelete('company', props.item)"
                      ><v-icon small>delete</v-icon></v-btn
                    >
                    <span>Delete company</span>
                  </v-tooltip>
                </td>
              </template>
              <template slot="no-data"
                ><div class="pa-4 text-xs-center">
                  No companies found
                </div></template
              >
            </v-data-table>
          </v-card>
        </v-tab-item>
      </v-tabs>
    </v-card>

    <v-dialog
      v-model="userDialog"
      max-width="760"
      persistent
      content-class="company-glass-dialog"
    >
      <v-card class="glass-modal form-modal">
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{
            editingUserId ? "Edit company user" : "Create company user"
          }}</v-toolbar-title>
          <v-spacer></v-spacer
          ><v-btn icon dark @click="closeUserDialog"
            ><v-icon>close</v-icon></v-btn
          >
        </v-toolbar>
        <v-card-text>
          <v-container class="modal-form-panel" grid-list-md>
            <v-layout row wrap>
              <v-flex xs12 sm6
                ><v-text-field
                  v-model.trim="userForm.first_name"
                  label="First name *"
                ></v-text-field
              ></v-flex>
              <v-flex xs12 sm6
                ><v-text-field
                  v-model.trim="userForm.last_name"
                  label="Last name *"
                ></v-text-field
              ></v-flex>
              <v-flex xs12 sm6
                ><v-text-field
                  v-model.trim="userForm.tel"
                  label="Telephone *"
                  prefix="0"
                  maxlength="9"
                  counter="9"
                ></v-text-field
              ></v-flex>
              <v-flex xs12 sm6
                ><v-text-field
                  v-model.trim="userForm.email"
                  label="Email (optional)"
                  type="email"
                ></v-text-field
              ></v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="userForm.password"
                  :label="
                    editingUserId
                      ? 'New password (leave blank to keep)'
                      : 'Password (optional)'
                  "
                  type="password"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6
                ><v-select
                  v-model="userForm.status"
                  :items="userStatusItems"
                  label="Status"
                ></v-select
              ></v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions class="modal-form-actions"
          ><v-spacer></v-spacer
          ><v-btn flat @click="closeUserDialog">Cancel</v-btn
          ><v-btn
            class="save-modal-btn"
            color="primary"
            :loading="saving"
            @click="saveUser"
            >Save</v-btn
          ></v-card-actions
        >
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="companyDialog"
      max-width="900"
      persistent
      content-class="company-glass-dialog"
    >
      <v-card class="glass-modal form-modal">
        <v-toolbar dark color="primary">
          <v-toolbar-title>{{
            editingCompanyId ? "Edit company" : "Create company"
          }}</v-toolbar-title>
          <v-spacer></v-spacer
          ><v-btn icon dark @click="closeCompanyDialog"
            ><v-icon>close</v-icon></v-btn
          >
        </v-toolbar>
        <v-card-text>
          <v-container class="modal-form-panel" grid-list-md>
            <v-layout row wrap>
              <v-flex xs12>
                <v-autocomplete
                  v-model="companyForm.user_id"
                  :items="userOptions"
                  item-text="text"
                  item-value="value"
                  label="Company owner user *"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs12 sm6
                ><v-text-field
                  v-model.trim="companyForm.name"
                  label="Company name *"
                ></v-text-field
              ></v-flex>
              <v-flex xs12 sm6
                ><v-text-field
                  v-model.trim="companyForm.juristic_id"
                  label="Juristic ID"
                ></v-text-field
              ></v-flex>
              <v-flex xs12 sm6
                ><v-text-field
                  v-model.trim="companyForm.tel"
                  label="Telephone *"
                  prefix="0"
                  maxlength="9"
                  counter="9"
                ></v-text-field
              ></v-flex>
              <v-flex xs12 sm6
                ><v-text-field
                  v-model.trim="companyForm.email"
                  label="Email (optional)"
                  type="email"
                ></v-text-field
              ></v-flex>
              <v-flex xs12
                ><v-text-field
                  v-model.trim="companyForm.address"
                  label="Address"
                ></v-text-field
              ></v-flex>
              <v-flex xs12 sm6
                ><v-text-field
                  v-model.trim="companyForm.bank_number"
                  label="Bank account number"
                ></v-text-field
              ></v-flex>
              <v-flex xs12 sm6
                ><v-select
                  v-model="companyForm.status"
                  :items="companyStatusItems"
                  label="Status"
                ></v-select
              ></v-flex>
              <v-flex xs12
                ><v-textarea
                  v-model.trim="companyForm.remark"
                  label="Remark"
                  rows="2"
                ></v-textarea
              ></v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions class="modal-form-actions"
          ><v-spacer></v-spacer
          ><v-btn flat @click="closeCompanyDialog">Cancel</v-btn
          ><v-btn
            class="save-modal-btn"
            color="primary"
            :loading="saving"
            @click="saveCompany"
            >Save</v-btn
          ></v-card-actions
        >
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="confirmDialog"
      max-width="520"
      persistent
      content-class="company-glass-dialog"
    >
      <v-card class="glass-modal form-modal">
        <v-card-title class="headline">{{ confirmTitle }}</v-card-title>
        <v-card-text>{{ confirmMessage }}</v-card-text>
        <v-card-actions class="modal-form-actions"
          ><v-spacer></v-spacer
          ><v-btn flat @click="clearConfirmation">Cancel</v-btn
          ><v-btn color="error" :loading="saving" @click="runConfirmedAction"
            >Confirm</v-btn
          ></v-card-actions
        >
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="secretDialog"
      max-width="720"
      persistent
      content-class="company-glass-dialog"
    >
      <v-card class="glass-modal form-modal">
        <v-toolbar dark color="warning">
          <v-toolbar-title>{{ secretTitle }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-alert :value="true" type="warning" outline>
            Copy this secret key now. It is shown only once and cannot be
            retrieved again.
          </v-alert>
          <div class="caption mb-3">
            The company must have Approved status before this key can
            authenticate API requests.
          </div>
          <v-textarea
            :value="secretValue"
            label="Secret key"
            readonly
            auto-grow
            rows="3"
            append-icon="content_copy"
            @click:append="copySecret"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="modal-form-actions">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="copySecret"
            ><v-icon left>content_copy</v-icon>Copy</v-btn
          >
          <v-btn flat @click="closeSecretDialog">I have saved it</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4500">{{
      message
    }}</v-snackbar>
  </v-container>
</template>

<script>
import moment from "moment";
import { globalAxios } from "@/axios-path.js";

const API_BASE = "/api/v1/providerwesmart/backoffice";

function emptyUserForm() {
  return {
    first_name: "",
    last_name: "",
    tel: "",
    email: "",
    password: "",
    status: 1,
  };
}

function emptyCompanyForm() {
  return {
    user_id: null,
    name: "",
    email: "",
    tel: "",
    status: 2,
    address: "",
    bank_number: "",
    customer_id_card: "",
    juristic_id: "",
    province_id: null,
    district_id: null,
    sub_district_id: null,
    zipcode_id: null,
    remark: "",
  };
}

export default {
  data() {
    return {
      activeTab: "users",
      loading: false,
      saving: false,
      users: [],
      companies: [],
      userSearch: "",
      companySearch: "",
      userDialog: false,
      companyDialog: false,
      confirmDialog: false,
      secretDialog: false,
      editingUserId: null,
      editingCompanyId: null,
      userForm: emptyUserForm(),
      companyForm: emptyCompanyForm(),
      pendingAction: null,
      confirmTitle: "",
      confirmMessage: "",
      secretTitle: "Secret key",
      secretValue: "",
      snackbar: false,
      snackbarColor: "success",
      message: "",
      userStatusItems: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      companyStatusItems: [
        { text: "Pending", value: 1 },
        { text: "Approved", value: 2 },
        { text: "Rejected", value: 3 },
        { text: "Edit document", value: 5 },
      ],
      userHeaders: [
        { text: "ID", value: "user_provider_wesmart_id" },
        { text: "Name", value: "user_provider_wesmart_first_name" },
        { text: "Telephone", value: "user_provider_wesmart_tel" },
        { text: "Email", value: "user_provider_wesmart_email" },
        {
          text: "Companies",
          value: "companies",
          align: "center",
          sortable: false,
        },
        { text: "Status", value: "user_provider_wesmart_status" },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      companyHeaders: [
        { text: "ID", value: "company_provider_wesmart_id" },
        { text: "Company", value: "company_provider_wesmart_name" },
        { text: "Owner", value: "company_provider_wesmart_user_id" },
        { text: "Telephone", value: "company_provider_wesmart_tel" },
        {
          text: "Available credit",
          value: "credit_available_balance",
          align: "right",
        },
        { text: "Status", value: "company_provider_wesmart_status" },
        { text: "Secret updated", value: "secret_updated" },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    filteredUsers() {
      const query = this.userSearch.trim().toLowerCase();
      if (!query) return this.users;
      return this.users.filter((item) =>
        [
          item.user_provider_wesmart_id,
          this.userName(item),
          item.user_provider_wesmart_tel,
          item.user_provider_wesmart_email,
        ].some(
          (value) =>
            String(value || "")
              .toLowerCase()
              .indexOf(query) !== -1
        )
      );
    },
    filteredCompanies() {
      const query = this.companySearch.trim().toLowerCase();
      if (!query) return this.companies;
      return this.companies.filter((item) =>
        [
          item.company_provider_wesmart_id,
          item.company_provider_wesmart_name,
          item.company_provider_wesmart_tel,
          item.company_provider_wesmart_email,
          this.ownerName(item.company_provider_wesmart_user_id),
        ].some(
          (value) =>
            String(value || "")
              .toLowerCase()
              .indexOf(query) !== -1
        )
      );
    },
    userOptions() {
      return this.users.map((item) => ({
        text:
          this.userName(item) +
          " · " +
          (item.user_provider_wesmart_tel ||
            item.user_provider_wesmart_email ||
            ""),
        value: item.user_provider_wesmart_id,
      }));
    },
  },
  created() {
    this.loadAll();
  },
  beforeDestroy() {
    this.secretValue = "";
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        const responses = await Promise.all([
          globalAxios.get(API_BASE + "/manage_business_users"),
          globalAxios.get(API_BASE + "/manage_company_info"),
        ]);
        this.users = (responses[0] && responses[0].data) || [];
        this.companies = (responses[1] && responses[1].data) || [];
      } catch (error) {
        this.notify(
          this.errorMessage(error, "Unable to load company management data"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    userName(item) {
      if (!item) return "-";
      return (
        (
          (item.user_provider_wesmart_first_name || "") +
          " " +
          (item.user_provider_wesmart_last_name || "")
        ).trim() || "-"
      );
    },
    ownerName(userId) {
      const user = this.users.find(
        (item) => Number(item.user_provider_wesmart_id) === Number(userId)
      );
      return user ? this.userName(user) : userId ? "User #" + userId : "-";
    },
    companyCount(userId) {
      return this.companies.filter(
        (item) =>
          Number(item.company_provider_wesmart_user_id) === Number(userId)
      ).length;
    },
    phone(value) {
      if (!value) return "-";
      const phone = String(value);
      return phone.charAt(0) === "0" ? phone : "0" + phone;
    },
    money(value) {
      return (
        "฿" +
        Number(value || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
    userStatusLabel(value) {
      return Number(value) === 1 ? "Active" : "Inactive";
    },
    userStatusColor(value) {
      return Number(value) === 1 ? "success" : "grey";
    },
    companyStatusLabel(value) {
      return (
        {
          1: "Pending",
          2: "Approved",
          3: "Rejected",
          4: "Deleted",
          5: "Edit document",
        }[Number(value)] || "Unknown"
      );
    },
    companyStatusColor(value) {
      return (
        { 1: "warning", 2: "success", 3: "error", 4: "grey", 5: "info" }[
          Number(value)
        ] || "grey"
      );
    },
    secretUpdatedAt(company) {
      const secrets = company.company_secrets || [];
      if (!secrets.length) return "Never";
      return moment(secrets[0].update_at || secrets[0].create_at).format(
        "DD/MM/YYYY HH:mm"
      );
    },
    openCreateUser() {
      this.editingUserId = null;
      this.userForm = emptyUserForm();
      this.userDialog = true;
    },
    openEditUser(item) {
      this.editingUserId = item.user_provider_wesmart_id;
      this.userForm = {
        first_name: item.user_provider_wesmart_first_name || "",
        last_name: item.user_provider_wesmart_last_name || "",
        tel: item.user_provider_wesmart_tel || "",
        email: item.user_provider_wesmart_email || "",
        password: "",
        status: Number(item.user_provider_wesmart_status),
      };
      this.userDialog = true;
    },
    closeUserDialog() {
      if (!this.saving) {
        this.userDialog = false;
        this.userForm = emptyUserForm();
        this.editingUserId = null;
      }
    },
    async saveUser() {
      if (
        !this.userForm.first_name ||
        !this.userForm.last_name ||
        !this.userForm.tel
      ) {
        return this.notify("Complete all required user fields", "error");
      }
      if (String(this.userForm.tel).length > 9)
        return this.notify(
          "Telephone must be at most 9 characters after prefix 0",
          "error"
        );
      this.saving = true;
      try {
        const payload = Object.assign({}, this.userForm);
        payload.email = payload.email || null;
        if (this.editingUserId) payload.user_id = this.editingUserId;
        if (this.editingUserId && !payload.password) delete payload.password;
        if (this.editingUserId)
          await globalAxios.put(API_BASE + "/manage_business_users", payload);
        else
          await globalAxios.post(API_BASE + "/manage_business_users", payload);
        this.userDialog = false;
        this.notify(
          this.editingUserId ? "Company user updated" : "Company user created",
          "success"
        );
        this.editingUserId = null;
        this.userForm = emptyUserForm();
        await this.loadAll();
      } catch (error) {
        this.notify(
          this.errorMessage(error, "Unable to save company user"),
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    openCreateCompany() {
      this.editingCompanyId = null;
      this.companyForm = emptyCompanyForm();
      this.companyDialog = true;
    },
    openEditCompany(item) {
      this.editingCompanyId = item.company_provider_wesmart_id;
      this.companyForm = {
        user_id: item.company_provider_wesmart_user_id,
        name: item.company_provider_wesmart_name || "",
        email: item.company_provider_wesmart_email || "",
        tel: item.company_provider_wesmart_tel || "",
        status: Number(item.company_provider_wesmart_status),
        address: item.company_provider_wesmart_address || "",
        bank_number: item.company_provider_wesmart_bank_number || "",
        customer_id_card: item.company_provider_wesmart_customer_id_card || "",
        juristic_id: item.company_provider_wesmart_juristic_id || "",
        province_id: item.company_provider_wesmart_province_id,
        district_id: item.company_provider_wesmart_district_id,
        sub_district_id: item.company_provider_wesmart_sub_district_id,
        zipcode_id: item.company_provider_wesmart_zipcode_id,
        remark: item.company_provider_wemsart_remark || "",
      };
      this.companyDialog = true;
    },
    closeCompanyDialog() {
      if (!this.saving) {
        this.companyDialog = false;
        this.companyForm = emptyCompanyForm();
        this.editingCompanyId = null;
      }
    },
    async saveCompany() {
      if (
        !this.companyForm.user_id ||
        !this.companyForm.name ||
        !this.companyForm.tel
      ) {
        return this.notify(
          "Select an owner and complete all required company fields",
          "error"
        );
      }
      if (String(this.companyForm.tel).length > 9)
        return this.notify(
          "Telephone must be at most 9 characters after prefix 0",
          "error"
        );
      this.saving = true;
      try {
        const payload = Object.assign({}, this.companyForm);
        payload.email = payload.email || null;
        let response;
        if (this.editingCompanyId) {
          payload.company_id = this.editingCompanyId;
          response = await globalAxios.put(
            API_BASE + "/manage_company_info",
            payload
          );
        } else {
          response = await globalAxios.post(
            API_BASE + "/manage_company_info",
            payload
          );
        }
        const wasEditing = !!this.editingCompanyId;
        const createdCompanyName = this.companyForm.name;
        this.companyDialog = false;
        this.editingCompanyId = null;
        this.companyForm = emptyCompanyForm();
        await this.loadAll();
        if (
          !wasEditing &&
          response &&
          response.generated_secret &&
          response.generated_secret.secret_key
        ) {
          this.showSecret(
            "Secret key for " + createdCompanyName,
            response.generated_secret.secret_key
          );
        } else {
          this.notify(
            wasEditing ? "Company updated" : "Company created",
            "success"
          );
        }
      } catch (error) {
        this.notify(
          this.errorMessage(error, "Unable to save company"),
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    askDelete(type, item) {
      const isUser = type === "user";
      this.confirmTitle = isUser ? "Delete company user?" : "Delete company?";
      this.confirmMessage = isUser
        ? "This will remove " +
          this.userName(item) +
          " from the active company-user list."
        : "This will deactivate and remove " +
          item.company_provider_wesmart_name +
          " from the active company list.";
      this.pendingAction = { type: type, item: item };
      this.confirmDialog = true;
    },
    askRotate(company) {
      this.confirmTitle = "Rotate secret key?";
      this.confirmMessage =
        "The current key for " +
        company.company_provider_wesmart_name +
        " will stop working immediately. The new key will be shown only once.";
      this.pendingAction = { type: "rotate", item: company };
      this.confirmDialog = true;
    },
    clearConfirmation() {
      if (!this.saving) {
        this.confirmDialog = false;
        this.pendingAction = null;
      }
    },
    async runConfirmedAction() {
      if (!this.pendingAction) return;
      const action = this.pendingAction;
      this.saving = true;
      try {
        if (action.type === "user") {
          await globalAxios.delete(API_BASE + "/manage_business_users", {
            data: { user_id: action.item.user_provider_wesmart_id },
          });
          this.notify("Company user deleted", "success");
        } else if (action.type === "company") {
          await globalAxios.delete(API_BASE + "/manage_company_info", {
            params: { company_id: action.item.company_provider_wesmart_id },
          });
          this.notify("Company deleted", "success");
        } else if (action.type === "rotate") {
          const response = await globalAxios.put(
            API_BASE +
              "/company/" +
              action.item.company_provider_wesmart_id +
              "/regenerate_secret"
          );
          if (!response || !response.data || !response.data.secret_key)
            throw new Error("Secret key was not returned");
          this.showSecret(
            "New secret key for " + action.item.company_provider_wesmart_name,
            response.data.secret_key
          );
        }
        this.confirmDialog = false;
        this.pendingAction = null;
        await this.loadAll();
      } catch (error) {
        this.notify(
          this.errorMessage(error, "Unable to complete the action"),
          "error"
        );
      } finally {
        this.saving = false;
      }
    },
    showSecret(title, value) {
      this.secretTitle = title;
      this.secretValue = String(value || "");
      this.secretDialog = true;
    },
    closeSecretDialog() {
      this.secretDialog = false;
      this.secretValue = "";
      this.secretTitle = "Secret key";
    },
    async copySecret() {
      if (!this.secretValue) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText)
          await navigator.clipboard.writeText(this.secretValue);
        else {
          const input = document.createElement("textarea");
          input.value = this.secretValue;
          input.setAttribute("readonly", "");
          input.style.position = "fixed";
          input.style.opacity = "0";
          document.body.appendChild(input);
          input.select();
          document.execCommand("copy");
          document.body.removeChild(input);
        }
        this.notify("Secret key copied", "success");
      } catch (error) {
        this.notify(
          "Unable to copy automatically. Select and copy the key manually.",
          "error"
        );
      }
    },
    errorMessage(error, fallback) {
      if (error && error.response && error.response.data) {
        return (
          error.response.data.detail || error.response.data.data || fallback
        );
      }
      return error && error.message ? error.message : fallback;
    },
    notify(message, color) {
      this.message = String(message || "");
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.company-management-page {
  max-width: 1500px;
  min-height: 100vh;
  padding: 0 24px 40px;
}
.page-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 8px 24px;
}
.page-title {
  margin: 0 0 8px;
  color: #2c3e50;
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.1;
  text-shadow: 2px 3px 12px rgba(33, 33, 32, 0.25);
}
.page-subtitle {
  margin: 0;
  color: #7f8c8d;
  font-size: 1.15rem;
}
.hero-icon {
  display: flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.58);
  box-shadow: 0 14px 35px rgba(31, 45, 61, 0.12);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}
.hero-icon .v-icon {
  color: #f3b400;
  font-size: 38px;
}
.glass-card,
.table-panel {
  border: 1px solid rgba(255, 255, 255, 0.82) !important;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.76) !important;
  box-shadow: 0 16px 45px rgba(31, 45, 61, 0.1) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}
.management-shell {
  overflow: hidden;
}
.table-search {
  max-width: 420px;
}
.company-credit-balance {
  color: #2e7d32;
  font-size: 0.95rem;
  font-weight: 700;
  white-space: nowrap;
}

.company-credit-balance--negative {
  color: #c62828 !important;
}

.company-credit-reserved {
  margin-top: 2px;
  color: #8a94a3;
  font-size: 0.72rem;
  white-space: nowrap;
}
.company-glass-dialog .glass-modal {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0) !important;
  box-shadow: 0 24px 70px rgba(18, 28, 45, 0.28) !important;
  backdrop-filter: blur(26px);
  -webkit-backdrop-filter: blur(26px);
}

@media (max-width: 768px) {
  .company-management-page {
    padding: 0 12px 28px;
  }
  .page-hero {
    text-align: center;
  }
  .page-title {
    font-size: 2rem;
  }
  .page-subtitle {
    font-size: 1rem;
  }
  .hero-icon {
    display: none;
  }
}
</style>

<style>
.company-glass-dialog {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 48px) !important;
  border-radius: 20px;
  background: transparent !important;
  box-shadow: none !important;
  overflow: hidden !important;
  overscroll-behavior: none;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.company-glass-dialog .glass-modal {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.78) !important;
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.92) !important;
  box-shadow: 0 24px 70px rgba(18, 28, 45, 0.3) !important;
  backdrop-filter: blur(28px) saturate(135%);
  -webkit-backdrop-filter: blur(28px) saturate(135%);
}
.company-glass-dialog .glass-modal > .v-toolbar {
  border-bottom: 1px solid rgba(255, 255, 255, 0.34) !important;
  background: rgba(0, 0, 0, 0.9) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}
.company-glass-dialog .form-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  max-height: calc(100vh - 48px);
  overflow: hidden !important;
}
.company-glass-dialog .form-modal > .v-toolbar {
  position: relative;
  z-index: 3;
  flex: 0 0 auto;
  box-shadow: 0 8px 20px rgba(18, 28, 45, 0.18) !important;
}
.company-glass-dialog .form-modal > .v-card__title {
  position: relative;
  z-index: 3;
  flex: 0 0 auto;
}
.company-glass-dialog .form-modal > .v-card__text {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}
.company-glass-dialog .form-modal > .modal-form-actions {
  position: relative;
  z-index: 2;
  flex: 0 0 auto;
  border-top: 1px solid rgba(31, 41, 55, 0.12);
  box-shadow: 0 -8px 24px rgba(18, 28, 45, 0.1);
}
.company-glass-dialog .glass-modal > .v-card__text,
.company-glass-dialog .glass-modal > .v-card__actions {
  background: rgba(255, 255, 255, 0.56) !important;
}
.company-glass-dialog .glass-modal .v-input__slot {
  background: rgba(255, 255, 255, 0.52);
}
.company-glass-dialog .modal-form-panel {
  width: auto;
  margin: 12px 8px;
  padding: 24px !important;
  border: 1px solid rgba(31, 41, 55, 0.18);
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(18, 28, 45, 0.1);
}
.company-glass-dialog .modal-form-panel .v-input__slot {
  background: rgba(248, 249, 251, 0.96);
}
.company-glass-dialog .modal-form-actions {
  min-height: 76px;
  /* padding: 14px 24px 22px !important; */
}
.company-glass-dialog .save-modal-btn {
  min-width: 104px;
  border-radius: 24px;
  padding-right: 24px;
  padding-left: 24px;
  box-shadow: 0 8px 18px rgba(255, 193, 7, 0.34) !important;
  transform: translateY(-2px);
}
@media (max-width: 600px) {
  .company-glass-dialog .modal-form-panel {
    margin: 6px 0;
    padding: 16px !important;
  }
  .company-glass-dialog .modal-form-actions {
    padding: 12px 16px 20px !important;
  }
}
</style>
